import { toCanonicalTransitionName } from './transitionAliases';

/**
 * Transaction process graph for product orders:
 *   - default-purchase
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes an order for a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',
  REQUEST_PAYMENT_WITH_PAYPAL: 'transition/request-payment-with-paypal',
  REQUEST_BOOKING_WITHOUT_PAYOUT: 'transition/request-booking-without-payout',
  REQUEST_BOOKING_WITHOUT_PAYOUT_AFTER_INQUIRY:
    'transition/request-booking-without-payout-after-inquiry',
  REQUEST_BOOKING_WITHOUT_PAYOUT_AFTER_OFFER:
    'transition/request-booking-without-payout-after-offer',
  CONFIRM_BOOKING_WITHOUT_PAYOUT: 'transition/confirm-booking-without-payout',
  EXPIRE_PAYMENT_WITHOUT_PAYOUT: 'transition/expire-payment-without-payout',
  MARK_DELIVERED_WITHOUT_PAYOUT: 'transition/mark-delivered-without-payout',
  OPERATOR_MARK_DELIVERED_WITHOUT_PAYOUT: 'transition/operator-mark-delivered-without-payout',
  MARK_RECEIVED_FROM_PURCHASED_WITHOUT_PAYOUT:
    'transition/mark-received-from-purchased-without-payout',
  MARK_RECEIVED_WITHOUT_PAYOUT: 'transition/mark-received-without-payout',
  AUTO_MARK_RECEIVED_WITHOUT_PAYOUT: 'transition/auto-mark-received-without-payout',
  DISPUTE_WITHOUT_PAYOUT: 'transition/dispute-without-payout',
  OPERATOR_DISPUTE_WITHOUT_PAYOUT: 'transition/operator-dispute-without-payout',
  MARK_RECEIVED_FROM_DISPUTED_WITHOUT_PAYOUT:
    'transition/mark-received-from-disputed-without-payout',

  // Offer flow transitions for purchase listings
  TRANSITION_SEND_OFFER: 'transition/send-offer',
  TRANSITION_SEND_OFFER_AUTO_ELIGIBLE: 'transition/send-offer-auto-eligible',
  TRANSITION_ACCEPT_OFFER: 'transition/accept-offer',
  TRANSITION_ACCEPT_AUTO_ELIGIBLE_OFFER: 'transition/accept-auto-eligible-offer',
  TRANSITION_DECLINE_OFFER: 'transition/decline-offer',
  TRANSITION_DECLINE_AUTO_ELIGIBLE_OFFER: 'transition/decline-auto-eligible-offer',
  TRANSITION_COUNTER_OFFER: 'transition/counter-offer',
  TRANSITION_COUNTER_AUTO_ELIGIBLE_OFFER: 'transition/counter-auto-eligible-offer',
  TRANSITION_ACCEPT_AFTER_COUNTER_OFFER: 'transition/accept-after-counter-offer',
  TRANSITION_DECLINE_AFTER_COUNTER_OFFER: 'transition/decline-after-counter-offer',
  TRANSITION_AUTO_ACCEPT_OFFER: 'transition/auto-accept-offer',
  REQUEST_PAYMENT_AFTER_OFFER: 'transition/request-payment-after-offer',
  REQUEST_PAYMENT_AFTER_OFFER_WITH_PAYPAL: 'transition/request-payment-after-offer-with-paypal',
  TRANSITION_AUTO_EXPIRE_OFFER_PAYMENT: 'transition/auto-expire-offer-payment',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',
  REQUEST_PAYMENT_AFTER_INQUIRY_WITH_PAYPAL: 'transition/request-payment-after-inquiry-with-paypal',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',
  CONFIRM_PAYMENT_WITH_PAYPAL: 'transition/confirm-payment-with-paypal',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // Provider or opeartor can mark the product shipped/delivered
  MARK_DELIVERED: 'transition/mark-delivered',
  MARK_DELIVERED_WITH_PAYPAL: 'transition/mark-delivered-with-paypal',
  OPERATOR_MARK_DELIVERED: 'transition/operator-mark-delivered',
  OPERATOR_MARK_DELIVERED_WITH_PAYPAL: 'transition/operator-mark-delivered-with-paypal',

  // Legacy transition retained for historical transactions. New receive actions must happen after delivery.
  MARK_RECEIVED_FROM_PURCHASED: 'transition/mark-received-from-purchased',

  // Legacy automatic cancellation transitions retained for historical transaction state resolution.
  AUTO_CANCEL: 'transition/auto-cancel',
  AUTO_CANCEL_WITHOUT_PAYOUT: 'transition/auto-cancel-without-payout',

  // Operator can cancel the purchase before product has been marked as delivered / received
  CANCEL: 'transition/cancel',
  CANCEL_WITH_PAYPAL: 'transition/cancel-with-paypal',
  CANCEL_WITHOUT_PAYOUT: 'transition/cancel-without-payout',

  // The seller cancels an unshipped order (reason saved in protectedData).
  SELLER_CANCEL: 'transition/seller-cancel',
  SELLER_CANCEL_WITH_PAYPAL: 'transition/seller-cancel-with-paypal',
  SELLER_CANCEL_WITHOUT_PAYOUT: 'transition/seller-cancel-without-payout',

  // The server's daily check cancels orders not shipped within 14 days.
  AUTO_CANCEL_UNSHIPPED: 'transition/auto-cancel-unshipped',
  AUTO_CANCEL_UNSHIPPED_WITH_PAYPAL: 'transition/auto-cancel-unshipped-with-paypal',
  AUTO_CANCEL_UNSHIPPED_WITHOUT_PAYOUT: 'transition/auto-cancel-unshipped-without-payout',

  // If provider has marked the product delivered (e.g. shipped),
  // customer can then mark the product received
  MARK_RECEIVED: 'transition/mark-received',
  MARK_RECEIVED_WITH_PAYPAL: 'transition/mark-received-with-paypal',

  // If customer doesn't mark the product received manually, it can happen automatically
  AUTO_MARK_RECEIVED: 'transition/auto-mark-received',
  AUTO_MARK_RECEIVED_WITH_PAYPAL: 'transition/auto-mark-received-with-paypal',

  // When provider has marked the product delivered, customer or operator can dispute the transaction
  DISPUTE: 'transition/dispute',
  DISPUTE_WITH_PAYPAL: 'transition/dispute-with-paypal',
  OPERATOR_DISPUTE: 'transition/operator-dispute',
  OPERATOR_DISPUTE_WITH_PAYPAL: 'transition/operator-dispute-with-paypal',

  // If nothing is done to disputed transaction it ends up to Canceled state
  AUTO_CANCEL_FROM_DISPUTED: 'transition/auto-cancel-from-disputed',
  AUTO_CANCEL_FROM_DISPUTED_WITH_PAYPAL: 'transition/auto-cancel-from-disputed-with-paypal',
  AUTO_CANCEL_FROM_DISPUTED_WITHOUT_PAYOUT: 'transition/auto-cancel-from-disputed-without-payout',

  // Operator can cancel disputed transaction manually
  CANCEL_FROM_DISPUTED: 'transition/cancel-from-disputed',
  CANCEL_FROM_DISPUTED_WITH_PAYPAL: 'transition/cancel-from-disputed-with-paypal',
  CANCEL_FROM_DISPUTED_WITHOUT_PAYOUT: 'transition/cancel-from-disputed-without-payout',

  // Operator can mark the disputed transaction as received
  MARK_RECEIVED_FROM_DISPUTED: 'transition/mark-received-from-disputed',
  MARK_RECEIVED_FROM_DISPUTED_WITH_PAYPAL: 'transition/mark-received-from-disputed-with-paypal',

  // System moves transaction automatically from received state to complete state
  // This makes it possible to to add notifications to that single transition.
  AUTO_COMPLETE: 'transition/auto-complete',
  WALLET_UPDATED: 'transition/wallet-updated',
  // Legacy transition used by wallet-process transactions in older data.
  UPDATE_WALLET_BALANCE: 'transition/update-wallet-balance',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */

export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  OFFER_PENDING: 'offer-pending',
  OFFER_COUNTERED: 'offer-countered',
  OFFER_ACCEPTED: 'offer-accepted',
  OFFER_DECLINED: 'offer-declined',
  OFFER_PAYMENT_EXPIRED: 'offer-payment-expired',
  PENDING_PAYMENT: 'pending-payment',
  PENDING_PAYMENT_WITHOUT_PAYOUT: 'pending-payment-without-payout',
  PAYMENT_EXPIRED: 'payment-expired',
  PAYMENT_EXPIRED_WITHOUT_PAYOUT: 'payment-expired-without-payout',
  PURCHASED: 'purchased',
  PURCHASED_WITHOUT_PAYOUT: 'purchased-without-payout',
  DELIVERED: 'delivered',
  DELIVERED_WITHOUT_PAYOUT: 'delivered-without-payout',
  RECEIVED_WITHOUT_PAYOUT: 'received-without-payout',
  RECEIVED: 'received',
  DISPUTED: 'disputed',
  DISPUTED_WITHOUT_PAYOUT: 'disputed-without-payout',
  CANCELED: 'canceled',
  COMPLETED: 'completed',
  WALLET_UPDATED: 'wallet-updated',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-purchase/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
        [transitions.REQUEST_PAYMENT_WITH_PAYPAL]: states.PENDING_PAYMENT,
        [transitions.REQUEST_BOOKING_WITHOUT_PAYOUT]: states.PENDING_PAYMENT,
        [transitions.TRANSITION_SEND_OFFER]: states.OFFER_PENDING,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY_WITH_PAYPAL]: states.PENDING_PAYMENT,
        [transitions.REQUEST_BOOKING_WITHOUT_PAYOUT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
      },
    },
    [states.OFFER_PENDING]: {
      on: {
        [transitions.TRANSITION_ACCEPT_OFFER]: states.OFFER_ACCEPTED,
        [transitions.TRANSITION_DECLINE_OFFER]: states.OFFER_DECLINED,
        [transitions.TRANSITION_COUNTER_OFFER]: states.OFFER_COUNTERED,
        [transitions.TRANSITION_AUTO_ACCEPT_OFFER]: states.OFFER_ACCEPTED,
      },
    },
    [states.OFFER_COUNTERED]: {
      on: {
        [transitions.TRANSITION_ACCEPT_AFTER_COUNTER_OFFER]: states.OFFER_ACCEPTED,
        [transitions.TRANSITION_DECLINE_AFTER_COUNTER_OFFER]: states.OFFER_DECLINED,
      },
    },
    [states.OFFER_ACCEPTED]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_OFFER]: states.PENDING_PAYMENT,
        [transitions.REQUEST_PAYMENT_AFTER_OFFER_WITH_PAYPAL]: states.PENDING_PAYMENT,
        [transitions.REQUEST_BOOKING_WITHOUT_PAYOUT_AFTER_OFFER]: states.PENDING_PAYMENT,
        [transitions.TRANSITION_AUTO_EXPIRE_OFFER_PAYMENT]: states.OFFER_PAYMENT_EXPIRED,
      },
    },
    [states.OFFER_DECLINED]: {},
    [states.OFFER_PAYMENT_EXPIRED]: {},

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.EXPIRE_PAYMENT_WITHOUT_PAYOUT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PURCHASED,
        [transitions.CONFIRM_PAYMENT_WITH_PAYPAL]: states.PURCHASED,
        [transitions.CONFIRM_BOOKING_WITHOUT_PAYOUT]: states.PURCHASED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PURCHASED]: {
      on: {
        [transitions.MARK_DELIVERED]: states.DELIVERED,
        [transitions.MARK_DELIVERED_WITHOUT_PAYOUT]: states.DELIVERED,
        [transitions.OPERATOR_MARK_DELIVERED]: states.DELIVERED,
        [transitions.OPERATOR_MARK_DELIVERED_WITHOUT_PAYOUT]: states.DELIVERED,
        [transitions.CANCEL]: states.CANCELED,
        [transitions.CANCEL_WITH_PAYPAL]: states.CANCELED,
        [transitions.CANCEL_WITHOUT_PAYOUT]: states.CANCELED,
        [transitions.SELLER_CANCEL]: states.CANCELED,
        [transitions.SELLER_CANCEL_WITH_PAYPAL]: states.CANCELED,
        [transitions.SELLER_CANCEL_WITHOUT_PAYOUT]: states.CANCELED,
        [transitions.AUTO_CANCEL_UNSHIPPED]: states.CANCELED,
        [transitions.AUTO_CANCEL_UNSHIPPED_WITH_PAYPAL]: states.CANCELED,
        [transitions.AUTO_CANCEL_UNSHIPPED_WITHOUT_PAYOUT]: states.CANCELED,
      },
    },

    [states.CANCELED]: {},

    [states.DELIVERED]: {
      on: {
        [transitions.MARK_RECEIVED]: states.RECEIVED,
        [transitions.MARK_RECEIVED_WITHOUT_PAYOUT]: states.RECEIVED_WITHOUT_PAYOUT,
        [transitions.AUTO_MARK_RECEIVED]: states.RECEIVED,
        [transitions.AUTO_MARK_RECEIVED_WITHOUT_PAYOUT]: states.RECEIVED_WITHOUT_PAYOUT,
        [transitions.DISPUTE]: states.DISPUTED,
        [transitions.DISPUTE_WITHOUT_PAYOUT]: states.DISPUTED,
        [transitions.OPERATOR_DISPUTE]: states.DISPUTED,
        [transitions.OPERATOR_DISPUTE_WITHOUT_PAYOUT]: states.DISPUTED,
      },
    },

    [states.DISPUTED]: {
      on: {
        [transitions.AUTO_CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.AUTO_CANCEL_FROM_DISPUTED_WITH_PAYPAL]: states.CANCELED,
        [transitions.AUTO_CANCEL_FROM_DISPUTED_WITHOUT_PAYOUT]: states.CANCELED,
        [transitions.CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.CANCEL_FROM_DISPUTED_WITH_PAYPAL]: states.CANCELED,
        [transitions.CANCEL_FROM_DISPUTED_WITHOUT_PAYOUT]: states.CANCELED,
        [transitions.MARK_RECEIVED_FROM_DISPUTED]: states.RECEIVED,
        [transitions.MARK_RECEIVED_FROM_DISPUTED_WITHOUT_PAYOUT]: states.RECEIVED_WITHOUT_PAYOUT,
      },
    },

    [states.RECEIVED_WITHOUT_PAYOUT]: {
      on: {
        [transitions.WALLET_UPDATED]: states.RECEIVED,
        [transitions.UPDATE_WALLET_BALANCE]: states.RECEIVED,
      },
    },

    [states.RECEIVED]: {
      on: {
        [transitions.AUTO_COMPLETE]: states.COMPLETED,
      },
    },

    [states.COMPLETED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.WALLET_UPDATED]: {},

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.TRANSITION_SEND_OFFER,
    transitions.TRANSITION_ACCEPT_OFFER,
    transitions.TRANSITION_DECLINE_OFFER,
    transitions.TRANSITION_COUNTER_OFFER,
    transitions.TRANSITION_ACCEPT_AFTER_COUNTER_OFFER,
    transitions.TRANSITION_DECLINE_AFTER_COUNTER_OFFER,
    transitions.TRANSITION_AUTO_ACCEPT_OFFER,
    transitions.TRANSITION_AUTO_EXPIRE_OFFER_PAYMENT,
    transitions.CONFIRM_PAYMENT,
    transitions.CONFIRM_PAYMENT_WITH_PAYPAL,
    transitions.AUTO_CANCEL,
    transitions.CANCEL,
    transitions.CANCEL_WITH_PAYPAL,
    transitions.SELLER_CANCEL,
    transitions.SELLER_CANCEL_WITH_PAYPAL,
    transitions.SELLER_CANCEL_WITHOUT_PAYOUT,
    transitions.AUTO_CANCEL_UNSHIPPED,
    transitions.AUTO_CANCEL_UNSHIPPED_WITH_PAYPAL,
    transitions.AUTO_CANCEL_UNSHIPPED_WITHOUT_PAYOUT,
    transitions.MARK_DELIVERED,
    transitions.MARK_DELIVERED_WITH_PAYPAL,
    transitions.OPERATOR_MARK_DELIVERED,
    transitions.OPERATOR_MARK_DELIVERED_WITH_PAYPAL,
    transitions.DISPUTE,
    transitions.DISPUTE_WITH_PAYPAL,
    transitions.OPERATOR_DISPUTE,
    transitions.OPERATOR_DISPUTE_WITH_PAYPAL,
    transitions.AUTO_COMPLETE,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.AUTO_CANCEL_FROM_DISPUTED_WITH_PAYPAL,
    transitions.CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED_WITH_PAYPAL,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
  ].includes(transition);
};
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    transitions.REQUEST_PAYMENT_AFTER_OFFER,
    transitions.REQUEST_PAYMENT_WITH_PAYPAL,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY_WITH_PAYPAL,
    transitions.REQUEST_PAYMENT_AFTER_OFFER_WITH_PAYPAL,
    transitions.REQUEST_BOOKING_WITHOUT_PAYOUT,
    transitions.REQUEST_BOOKING_WITHOUT_PAYOUT_AFTER_INQUIRY,
    transitions.REQUEST_BOOKING_WITHOUT_PAYOUT_AFTER_OFFER,
    transitions.CONFIRM_BOOKING_WITHOUT_PAYOUT,
    transitions.CONFIRM_PAYMENT,
    transitions.CONFIRM_PAYMENT_WITH_PAYPAL,
  ].includes(transition);
};

// Check when transaction is completed (item is received and review notifications sent)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.AUTO_COMPLETE,
    transitions.WALLET_UPDATED,
    transitions.UPDATE_WALLET_BALANCE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (order did not happen).
// Auto-cancel is kept here for historical transaction compatibility.
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.CANCEL,
    transitions.CANCEL_WITH_PAYPAL,
    transitions.AUTO_CANCEL,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.AUTO_CANCEL_FROM_DISPUTED_WITH_PAYPAL,
    transitions.CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED_WITH_PAYPAL,
    transitions.SELLER_CANCEL,
    transitions.SELLER_CANCEL_WITH_PAYPAL,
    transitions.SELLER_CANCEL_WITHOUT_PAYOUT,
    transitions.AUTO_CANCEL_UNSHIPPED,
    transitions.AUTO_CANCEL_UNSHIPPED_WITH_PAYPAL,
    transitions.AUTO_CANCEL_UNSHIPPED_WITHOUT_PAYOUT,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [
  states.PURCHASED,
  states.PURCHASED_WITHOUT_PAYOUT,
  states.OFFER_PENDING,
];

const hasTransitionInHistory = (tx, targetTransition) =>
  (tx?.attributes?.transitions || []).some(
    transition => toCanonicalTransitionName(transition?.transition) === targetTransition
  );

const NO_PAYOUT_RECEIVE_LAST_TRANSITIONS = new Set([
  transitions.MARK_RECEIVED_FROM_PURCHASED_WITHOUT_PAYOUT,
  transitions.MARK_RECEIVED_WITHOUT_PAYOUT,
  transitions.AUTO_MARK_RECEIVED_WITHOUT_PAYOUT,
  transitions.MARK_RECEIVED_FROM_DISPUTED_WITHOUT_PAYOUT,
]);

export const resolveState = tx => {
  const lastTransition = tx?.attributes?.lastTransition;
  const canonicalLastTransition = toCanonicalTransitionName(tx?.attributes?.lastTransition);

  if (canonicalLastTransition === transitions.MARK_RECEIVED_FROM_PURCHASED) {
    return lastTransition === transitions.MARK_RECEIVED_FROM_PURCHASED_WITHOUT_PAYOUT
      ? states.RECEIVED_WITHOUT_PAYOUT
      : states.RECEIVED;
  }

  if (canonicalLastTransition === transitions.AUTO_CANCEL) {
    return states.CANCELED;
  }

  if (NO_PAYOUT_RECEIVE_LAST_TRANSITIONS.has(lastTransition)) {
    return states.RECEIVED_WITHOUT_PAYOUT;
  }

  if (canonicalLastTransition === transitions.WALLET_UPDATED) {
    return hasTransitionInHistory(tx, transitions.AUTO_COMPLETE)
      ? states.COMPLETED
      : states.RECEIVED;
  }

  return null;
};
